import React, { useEffect, useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, FileText, Phone, Calendar, ArrowRight, Check } from 'lucide-react';
import { FastTrackForm } from './FastTrackForm';
import { useLanguage } from '../utils/i18n';
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 40
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
export function ThankYouPage() {
  const {
    t
  } = useLanguage();
  const [showFastTrack, setShowFastTrack] = useState(false);
  const [fastTrackComplete, setFastTrackComplete] = useState(false);
  // Check if this was a recent resubmission (within 10 minutes)
  const checkRecentSubmission = () => {
    const keys = Object.keys(sessionStorage);
    const emailJsKeys = keys.filter(key => key.startsWith('dermaclinic_emailjs_sent_'));
    if (emailJsKeys.length > 0) {
      const lastKey = emailJsKeys[emailJsKeys.length - 1];
      const lastSubmitTime = sessionStorage.getItem(lastKey);
      if (lastSubmitTime) {
        const timeSinceLastSubmit = Date.now() - parseInt(lastSubmitTime);
        const tenMinutes = 10 * 60 * 1000;
        return timeSinceLastSubmit < tenMinutes;
      }
    }
    return false;
  };
  const isRecentResubmission = checkRecentSubmission();
  const handleAccelerateClick = () => {
    setShowFastTrack(true);
  };
  const handleFastTrackSubmit = () => {
    setFastTrackComplete(true);
    setShowFastTrack(false);
  };
  const handleSkip = () => {
    setShowFastTrack(false);
  };
  return <div className="min-h-screen w-full bg-white" data-id="element-525" data-mp="475xizuzrq0u">
      {/* Header with Logo */}
      <header className="border-b border-[#E6D3B3]/30 bg-white" data-id="element-526" data-mp="1tmdgxmrp2by4">
        <div className="max-w-7xl mx-auto px-4 py-4" data-id="element-527" data-mp="1ylgmd8bl6k6m">
          <img src="https://cdn.magicpatterns.com/uploads/g5pmrSprDDvvcpEJz6NJvt/logo-b_w-small.png" alt="DermaClinic" className="h-10 md:h-12 w-auto" data-id="element-528" data-mp="1yiuflawis8pg" />
        </div>
      </header>

      <div className="px-4 py-20" data-id="element-529" data-mp="1n5bz2buzau1h">
        <div className="max-w-4xl mx-auto" data-id="element-530" data-mp="1n6n56ibwcdvr">
          <AnimatePresence mode="wait" data-id="element-531" data-mp="1n7ex9j6abtha">
            {!showFastTrack ? <motion.div key="confirmation" initial="hidden" animate="visible" exit={{
            opacity: 0,
            y: -20
          }} variants={staggerContainer} data-id="element-532" data-mp="1j5te5g35qiyn">
                {/* BRAND CONFIRMATION ICON */}
                <motion.div variants={fadeInUp} className="mb-8" data-id="element-533" data-mp="222ukp5cd0r4p">
                  <div className="text-center" data-id="element-534" data-mp="222m8lfo29puk">
                    <div className="w-20 h-20 rounded-full bg-[#C9A961]/10 border-2 border-[#C9A961] flex items-center justify-center mx-auto shadow-sm" data-id="element-535" data-mp="2ylaeqyknwebo">
                      <img src="https://cdn.magicpatterns.com/uploads/g5pmrSprDDvvcpEJz6NJvt/logo-b_w-small.png" alt="DermaClinic" className="h-8 w-auto" data-id="element-536" data-mp="2trxq4ofzg7ix" />
                    </div>
                  </div>
                </motion.div>

                {/* CONFIRMATION STATUS BOX */}
                <motion.div variants={fadeInUp} className="mb-12" data-id="element-537" data-mp="ubyojdi6wiex">
                  <div className="flex items-center justify-center gap-3 p-6 bg-[#F7F4EF] rounded-xl border-2 border-[#E6D3B3] shadow-sm" data-id="element-538" data-mp="zas4rdov2nis">
                    <CheckCircle className="w-6 h-6 text-[#C9A961] flex-shrink-0" strokeWidth={1.75} data-id="element-539" data-mp="147satolpnq9w" />
                    <div className="text-left" data-id="element-540" data-mp="1d1wxrwxq4hk5">
                      <p className="text-base font-semibold text-[#2E2E2E] mb-1" data-id="element-541" data-mp="1d3rgd4livxl2">
                        {t('thankYou.statusTitle')}
                      </p>
                      <p className="text-sm text-[#555555]" data-id="element-542" data-mp="1n0w6ffihod75">
                        {isRecentResubmission ? t('thankYou.statusRecent') : t('thankYou.statusNew')}
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* CALL US NOW — HOT LEAD CAPTURE */}
                <motion.div variants={fadeInUp} className="mb-12" data-id="element-543" data-mp="1e3asxhl1zhhc">
                  <div className="relative overflow-hidden rounded-2xl border-2 border-[#C9A961] bg-gradient-to-br from-[#FFFDF7] to-[#FFF9ED] p-6 md:p-8 shadow-lg" data-id="element-544" data-mp="1e5x9g6px97bp">
                    {/* Decorative gold glow */}
                    <div className="absolute top-0 right-0 w-32 h-32 bg-[#C9A961]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none" data-id="element-545" data-mp="10cftevwg2cqf" />

                    <div className="relative z-10 text-center" data-id="element-546" data-mp="vc1i94zjqmo6">
                      {/* Personalized message */}
                      <p className="text-lg md:text-xl font-bold text-[#2E2E2E] mb-2" data-id="element-547" data-mp="2btp7t40puekv">
                        {t('thankYou.callNowIntro')}
                        {(() => {
                      try {
                        const data = JSON.parse(localStorage.getItem('dermaclinic_lead_data') || '{}');
                        const name = data.fullName?.split(' ')[0];
                        return name ? `, ${name}` : '';
                      } catch {
                        return '';
                      }
                    })()}
                        .
                      </p>
                      <p className="text-[#555555] mb-5 leading-relaxed" data-id="element-548" data-mp="2oa6vgmr91x5w">
                        {t('thankYou.callNowMessage')}
                      </p>

                      {/* Divider */}
                      <div className="flex items-center gap-3 mb-5" data-id="element-549" data-mp="3g1ean4fgd6sb">
                        <div className="flex-1 h-px bg-[#C9A961]/20" data-id="element-550" data-mp="3g0397b9hcb4d" />
                        <span className="text-sm font-semibold text-[#C9A961] uppercase tracking-wide" data-id="element-551" data-mp="3kt4npi5ry8nf">
                          {t('thankYou.callNowPrompt')}
                        </span>
                        <div className="flex-1 h-px bg-[#C9A961]/20" data-id="element-552" data-mp="3qm85pswh8qgp" />
                      </div>

                      {/* Phone button */}
                      <a href="tel:+17026604534" onClick={() => {
                    try {
                      if (typeof (window as any).fbq === 'function') {
                        ;
                        (window as any).fbq('trackCustom', 'ClickToCall', {
                          source: 'thank_you_page',
                          phone: '+17026604534'
                        });
                        console.log('PIXEL_CLICK_TO_CALL_FIRED');
                      }
                    } catch (e) {
                      console.warn('ClickToCall pixel event failed:', e);
                    }
                  }} className="inline-flex items-center justify-center gap-3 w-full sm:w-auto px-8 py-4 bg-[#C9A961] text-white rounded-full font-bold text-lg hover:bg-[#B09045] transition-all shadow-lg hover:shadow-xl active:scale-[0.98]" data-id="element-553" data-mp="3lphtjgnsmf0k">
                        <Phone className="w-5 h-5 animate-pulse" strokeWidth={2.5} data-id="element-554" data-mp="1v5glxugjg3fc" />
                        +1 (702) 660-4534
                      </a>

                      <p className="text-xs text-[#888888] mt-3" data-id="element-555" data-mp="252cxya5nb7on">
                        {t('thankYou.callNowButton')} — DermaClinic Center
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* NEXT STEPS SECTION */}
                <motion.div variants={fadeInUp} className="mb-12" data-id="element-556" data-mp="1hd0fskscbpa7">
                  <div className="bg-white rounded-2xl p-8 md:p-10 border-2 border-[#E6D3B3] shadow-lg" data-id="element-557" data-mp="2vka4qhn4gj1n">
                    <h2 className="text-2xl font-bold text-[#2E2E2E] mb-6 text-center" data-id="element-558" data-mp="2riomshvqdekl">
                      {t('thankYou.nextStepsTitle')}
                    </h2>

                    <div className="space-y-4 mb-6" data-id="element-559" data-mp="35jrat06acyy5">
                      <div className="flex items-start gap-3" data-id="element-560" data-mp="30j3jsei24i3x">
                        <div className="w-6 h-6 rounded-full bg-[#C9A961]/10 border border-[#C9A961] flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-561" data-mp="30lpom6dkcjig">
                          <Check className="w-4 h-4 text-[#C9A961]" strokeWidth={2} data-id="element-562" data-mp="3agrgvb34y1p9" />
                        </div>
                        <p className="text-[#555555] leading-relaxed" data-id="element-563" data-mp="3jb5t14sdcw45">
                          {t('thankYou.step1')}
                        </p>
                      </div>

                      <div className="flex items-start gap-3" data-id="element-564" data-mp="3t8k5ktb690r6">
                        <div className="w-6 h-6 rounded-full bg-[#C9A961]/10 border border-[#C9A961] flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-565" data-mp="3t67fttz69m3k">
                          <Check className="w-4 h-4 text-[#C9A961]" strokeWidth={2} data-id="element-566" data-mp="koqc7alwahb2" />
                        </div>
                        <p className="text-[#555555] leading-relaxed" data-id="element-567" data-mp="bo1wmbgus8w2">
                          {t('thankYou.step2')}
                        </p>
                      </div>

                      <div className="flex items-start gap-3" data-id="element-568" data-mp="1rp6toofif6l">
                        <div className="w-6 h-6 rounded-full bg-[#C9A961]/10 border border-[#C9A961] flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-569" data-mp="1t089huejauj">
                          <Check className="w-4 h-4 text-[#C9A961]" strokeWidth={2} data-id="element-570" data-mp="3p517stoenhpa" />
                        </div>
                        <p className="text-[#555555] leading-relaxed" data-id="element-571" data-mp="3f50np5ipoxk1">
                          {t('thankYou.step3')}
                        </p>
                      </div>
                    </div>

                    <p className="text-xs text-[#888888] text-center leading-relaxed" data-id="element-572" data-mp="it4xvca5j856">
                      {t('thankYou.checkSpam')}
                    </p>
                  </div>
                </motion.div>

                {/* WHAT TO EXPECT SECTION */}
                <motion.div variants={fadeInUp} className="bg-[#F7F4EF] rounded-2xl p-8 md:p-10 mb-12 border-2 border-[#E6D3B3]" data-id="element-573" data-mp="wji5ten1mmui">
                  <h2 className="text-2xl font-bold text-[#2E2E2E] mb-8 text-center" data-id="element-574" data-mp="11kfkhh8ugyr9">
                    {t('thankYou.expectTitle')}
                  </h2>

                  <div className="space-y-6" data-id="element-575" data-mp="rrgtxttsey50">
                    {[{
                  icon: FileText,
                  title: t('thankYou.expect1Title'),
                  desc: t('thankYou.expect1Desc')
                }, {
                  icon: Phone,
                  title: t('thankYou.expect2Title'),
                  desc: t('thankYou.expect2Desc')
                }, {
                  icon: Calendar,
                  title: t('thankYou.expect3Title'),
                  desc: t('thankYou.expect3Desc')
                }].map((item, idx) => <div key={idx} className="flex items-start space-x-4" data-id="element-576" data-mp="2m2q1p4as4eqb" data-mpi={idx}>
                        <div className="w-12 h-12 rounded-full bg-white border-2 border-[#E6D3B3] flex items-center justify-center flex-shrink-0 shadow-sm" data-id="element-577" data-mp="2h2u5vsc8jtlu">
                          <item.icon className="w-6 h-6 text-[#C9A961]" strokeWidth={1.75} data-id="element-578" data-mp="2qzyoxjasb8yf" />
                        </div>
                        <div className="flex-1" data-id="element-579" data-mp="6a0u35inw1qb">
                          <h3 className="font-semibold text-[#2E2E2E] mb-1 text-lg" data-id="element-580" data-mp="697xuyhbwumf">
                            {item.title}
                          </h3>
                          <p className="text-sm text-[#555555] leading-relaxed" data-id="element-581" data-mp="3429n1yp2tp6i">
                            {item.desc}
                          </p>
                        </div>
                      </div>)}
                  </div>
                </motion.div>

                {/* WHATSAPP FALLBACK */}
                <motion.div variants={fadeInUp} className="mb-8" data-id="element-582" data-mp="2gernhgzklmsz">
                  <p className="text-sm text-[#888888] text-center leading-relaxed" data-id="element-583" data-mp="2lc1pdw45bqxp">
                    {t('thankYou.checkSpam')}
                  </p>
                </motion.div>

                {/* CONTACT INFO */}
                <motion.div variants={fadeInUp} className="text-center" data-id="element-584" data-mp="2u5wskylpj7ap">
                  <p className="text-sm text-[#888888] mb-2" data-id="element-585" data-mp="1xn8qprw534ze">
                    {t('thankYou.contactTitle')}
                  </p>
                  <p className="text-base text-[#2E2E2E] font-medium" data-id="element-586" data-mp="21n9dbzte6mhj">
                    {t('thankYou.contactSubtitle')}
                  </p>
                </motion.div>
              </motion.div> : <motion.div key="fasttrack" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} data-id="element-587" data-mp="1p6rjfzc9m7o2">
                <FastTrackForm onSubmit={handleFastTrackSubmit} onSkip={handleSkip} data-id="element-588" data-mp="16efhvetfraez" />
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-12 px-4 bg-[#F7F4EF] border-t border-[#E6D3B3]/30" data-id="element-589" data-mp="xczbdi5qrsg1">
        <div className="max-w-7xl mx-auto text-center" data-id="element-590" data-mp="shsaei3ux1ut">
          <img src="https://cdn.magicpatterns.com/uploads/g5pmrSprDDvvcpEJz6NJvt/logo-b_w-small.png" alt="DermaClinic" className="h-8 w-auto mx-auto mb-4 opacity-60" data-id="element-591" data-mp="ngcrowp5j7fl" />
          <p className="text-xs text-[#888888]" data-id="element-592" data-mp="2sgniszpycz7d">{t('thankYou.footer')}</p>
        </div>
      </footer>
    </div>;
}